/**
 * Post-LAUNCH asset gate. Pure state in computeLoaderState (unit-tested).
 *
 * Loading is not a separate overlay — it is a PHASE of the title screen. It
 * reuses the very same line motif, starfield and black background, so logging in
 * never re-renders the line: it just switches the existing strands into loading
 * mode and they converge to show progress, then merge into the single line,
 * which fades away, then the black background fades to reveal the game.
 *
 * Anims are awaited (never skipped — a T-pose is worse than waiting); terrain
 * coverage has an 8 s cap.
 */
import type { MeridianLine } from "./meridianLine";

const TERRAIN_CAP_MS = 8000;
/** Anims weigh 60% of the bar, terrain 40% (terrain is mostly pre-warmed under the title). */
const ANIM_WEIGHT = 0.6;

export interface LoaderInput {
  animLoaded: number;
  animTotal: number;
  terrainOk: boolean;
  elapsedMs: number;
}

export function computeLoaderState(p: LoaderInput): { frac: number; label: string; done: boolean } {
  const animFrac = p.animTotal > 0 ? p.animLoaded / p.animTotal : 1;
  const animsDone = animFrac >= 1;
  const frac = Math.min(1, animFrac * ANIM_WEIGHT + (p.terrainOk ? 1 - ANIM_WEIGHT : 0));
  const done = animsDone && (p.terrainOk || p.elapsedMs > TERRAIN_CAP_MS);
  const label = !animsDone ? "LOADING ANIMATIONS" : p.terrainOk ? "READY" : "MESHING TERRAIN";
  return { frac, label, done };
}

export class LoadScreen {
  private intro: HTMLElement | null = null;
  private line: MeridianLine | null = null;
  private status: HTMLElement | null = null;
  private lastLabel = "";
  private timers: number[] = [];

  /** Wire up the shared title line + intro overlay + status label (once, at startup). */
  attach(intro: HTMLElement, line: MeridianLine, status: HTMLElement): void {
    this.intro = intro;
    this.line = line;
    this.status = status;
  }

  /** Enter the loading phase of the title: keep the SAME line, just start it
   *  converging. No flare, no reset — the strands carry straight over. */
  show(): void {
    if (!this.intro || !this.line) return;
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.intro.classList.remove("hidden"); // the title IS the loading screen
    this.line.el.style.opacity = "1";
    this.line.setMode("loading");
    this.line.setProgress(0);
    this.line.start();
    if (this.status) this.status.textContent = "LOADING…";
    this.lastLabel = "LOADING…";
    this.intro.dataset.view = "loading";
  }

  setProgress(frac: number, label: string): void {
    if (!this.line) return;
    this.line.setProgress(frac);
    if (label !== this.lastLabel) {
      this.lastLabel = label;
      if (this.status) this.status.textContent = label;
      this.line.stir(0.4); // the line reacts each time the phase changes
    }
  }

  /**
   * Staged outro once loading completes: finish merging into one line, fade the
   * line away, THEN fade the black background to reveal the game — then quietly
   * restore the title line to its resting state for any later re-show.
   */
  hide(): void {
    if (!this.intro || !this.line) return;
    this.timers.forEach(clearTimeout);
    const line = this.line;
    const intro = this.intro;
    line.setProgress(1); // drive the last of the merge into one line
    this.timers = [
      window.setTimeout(() => { line.el.style.opacity = "0"; }, 220), // fade the line
      window.setTimeout(() => intro.classList.add("hidden"), 700), // then the black bg → game
      window.setTimeout(() => {
        line.el.style.opacity = "1";
        line.setMode("title");
        intro.dataset.view = "brand";
      }, 1550),
    ];
  }

  /** Abort loading and restore the title line (used when a launch fails and the
   *  title is shown again). Leaves intro visibility/view to the caller. */
  cancel(): void {
    if (!this.line) return;
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.line.el.style.opacity = "1";
    this.line.setMode("title");
    this.line.start();
  }
}
